// src/components/games/crossroads/CrossroadsMenu.tsx
// Main menu for Crossroads TCG - choose single or multiplayer

import React, { useRef, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButton,
  IonIcon,
  IonButtons,
  IonText,
  IonModal,
} from '@ionic/react';
import {
  arrowBack,
  personOutline,
  peopleOutline,
  gameControllerOutline,
  helpCircleOutline,
  informationCircleOutline,
  openOutline,
} from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import './CrossroadsMenu.css';

export const CrossroadsMenu: React.FC = () => {
  const history = useHistory();
  const howToPlayRef = useRef<HTMLIonCardElement>(null);
  const [showDivergentRealmsModal, setShowDivergentRealmsModal] = useState(false);

  const scrollToHowToPlay = () => {
    howToPlayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => history.goBack()}>
              <IonIcon icon={arrowBack} />
            </IonButton>
          </IonButtons>
          <IonTitle>Crossroads: The Divergent Realms TCG</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={scrollToHowToPlay}>
              <IonIcon icon={helpCircleOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="crossroads-menu">
        <div className="crossroads-menu-container">
          {/* Header */}
          <IonCard className="crossroads-header-card">
            <IonCardContent>
              <div className="crossroads-header-content">
                <div className="crossroads-header-icon">🎴</div>
                <div>
                  <h1>Crossroads: The Divergent Realms TCG</h1>
                  <p>
                    Strategic card battler with elemental commanders, creatures, and spells.
                    Build your deck and dominate the battlefield!
                  </p>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Game Modes */}
          <div className="game-modes">
            {/* Single Player */}
            <IonCard button onClick={() => history.push('/games/crossroads/singleplayer')} className="mode-card">
              <IonCardHeader>
                <div className="mode-card-header">
                  <IonIcon icon={personOutline} className="mode-icon" />
                  <IonCardTitle>Single Player</IonCardTitle>
                </div>
              </IonCardHeader>
              <IonCardContent>
                <p>Battle against AI opponents with three difficulty levels. Perfect your strategy!</p>
                <div className="mode-features">
                  <div className="feature">✓ Choose your deck</div>
                  <div className="feature">✓ 3 AI difficulties</div>
                  <div className="feature">✓ Practice mode</div>
                  <div className="feature">✓ Tutorial included</div>
                </div>
                <IonButton expand="block" color="primary" style={{ marginTop: '12px' }}>
                  <IonIcon icon={gameControllerOutline} slot="start" />
                  Play vs AI
                </IonButton>
              </IonCardContent>
            </IonCard>

            {/* Multiplayer */}
            <IonCard button onClick={() => history.push('/games/crossroads/multiplayer')} className="mode-card">
              <IonCardHeader>
                <div className="mode-card-header">
                  <IonIcon icon={peopleOutline} className="mode-icon" />
                  <IonCardTitle>Multiplayer</IonCardTitle>
                </div>
              </IonCardHeader>
              <IonCardContent>
                <p>Challenge friends to intense PvP battles. Real-time turn-based combat!</p>
                <div className="mode-features">
                  <div className="feature">✓ Challenge friends</div>
                  <div className="feature">✓ Real-time sync</div>
                  <div className="feature">✓ Match history</div>
                  <div className="feature">✓ Player stats</div>
                </div>
                <IonButton expand="block" color="success" style={{ marginTop: '12px' }}>
                  <IonIcon icon={peopleOutline} slot="start" />
                  Play Online
                </IonButton>
              </IonCardContent>
            </IonCard>
          </div>

          {/* What is Divergent Realms? */}
          <IonCard className="info-card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', marginBottom: '16px' }}>
            <IonCardContent>
              <IonButton
                expand="block"
                color="light"
                size="large"
                onClick={() => setShowDivergentRealmsModal(true)}
              >
                <IonIcon icon={informationCircleOutline} slot="start" />
                What is Divergent Realms?
              </IonButton>
            </IonCardContent>
          </IonCard>

          {/* How to Play */}
          <IonCard className="info-card" ref={howToPlayRef}>
            <IonCardHeader>
              <IonCardTitle>How to Play</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="how-to-play">
                <div className="step">
                  <strong>1. Choose Your Commander</strong>
                  <p>Each commander has unique abilities and starting health</p>
                </div>
                <div className="step">
                  <strong>2. Build Your Deck</strong>
                  <p>25 cards from your chosen element - creatures, spells, and mana sources</p>
                </div>
                <div className="step">
                  <strong>3. Battle!</strong>
                  <p>Play mana, summon creatures, cast spells. Reduce opponent's commander to 0 health to win</p>
                </div>
                <div className="step">
                  <strong>4. Master the Elements</strong>
                  <p>Fire 🔥 | Earth 🌱 | Water 💧 | Wind 💨 | Light ☀️ | Dark 🌑 | Fae ✨</p>
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Footer */}
          <IonText color="medium" style={{ textAlign: 'center', padding: '16px' }}>
            <p style={{ fontSize: '0.9em', margin: 0 }}>
              New to Crossroads? Start with <strong>Single Player</strong> to learn the game!
            </p>
          </IonText>
        </div>

        {/* Divergent Realms Info Modal */}
        <IonModal isOpen={showDivergentRealmsModal} onDidDismiss={() => setShowDivergentRealmsModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>About Divergent Realms</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowDivergentRealmsModal(false)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>🎴 Divergent Realms</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div style={{ marginBottom: '16px' }}>
                  <p>
                    <strong>Divergent Realms</strong> is a partner of Dice Cat — a community built by and for
                    neurodivergent gamers. They offer paid tabletop RPG adventures led by trained neurodivergent
                    Game Masters and supported by a licensed counselor.
                  </p>
                  <p>
                    <em>"Every neurodivergent journey is a hero's journey!"</em>
                  </p>
                  <p>
                    <strong>What They Offer:</strong>
                  </p>
                  <ul style={{ marginLeft: '20px', marginBottom: '12px' }}>
                    <li>Custom campaigns and standard modules for D&D, Pathfinder, and more</li>
                    <li>Open tables for campaigns and one-shot adventures</li>
                    <li>GM coaching and character creation help</li>
                    <li>Safe spaces where you can unmask and be yourself</li>
                    <li>Memberships starting at just $1/month</li>
                  </ul>
                  <p style={{ fontSize: '0.9em', fontStyle: 'italic' }}>
                    No official diagnosis required — if you're comfortable here, you belong!
                  </p>
                </div>

                <IonButton
                  expand="block"
                  color="primary"
                  href="https://www.divergentrealms.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IonIcon icon={openOutline} slot="start" />
                  Visit Divergent Realms Website
                </IonButton>

                <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(103, 126, 234, 0.1)', borderRadius: '8px' }}>
                  <IonText color="medium">
                    <p style={{ fontSize: '0.9em', margin: 0 }}>
                      <strong>Partnership:</strong> Dice Cat and Divergent Realms have a mutual advertising
                      partnership. We feature them in Dice Cat and use some of their assets in our TCG game,
                      and they run ads for us. It's a win-win for both communities!
                    </p>
                  </IonText>
                </div>
              </IonCardContent>
            </IonCard>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default CrossroadsMenu;
